import { DetailedHTMLProps, HTMLAttributes, memo, MouseEventHandler, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useMedia } from 'react-use';

import { getIsMobile } from '~/features/navigation/components/N20MainMenu/components/helpers';
import MainMenuSubmenu from '~/features/navigation/components/N20MainMenu/components/MainMenuNav/mainMenuSubmenu';
import { transformTrackingData } from '~/features/navigation/components/N20MainMenu/components/MainMenuNav/transformTrackingData.helper';
import { triggerMenuCollapseOnElement } from '~/features/navigation/components/N20MainMenu/N20MainMenu';
import useMainMenuState from '~/features/navigation/components/N20MainMenu/useMainMenuState';
import { CeBrandNavigationEvent, CeNavigationTrackingData } from '~/features/tracking/model';
import { pushToDataLayer } from '~/features/tracking/utils/pushToDataLayer';
import ChevronDownIcon from '~/icons/chevron-down.svg';
import { IBrandVertical } from '~/lib/data-contract';
import { IMultiLevelNavigationNode, IVerticalMenu } from '~/lib/data-contract';
import { lgCondition } from '~/theme/lib/breakpoints';

import styles from './mainMenuNav.module.css';

type MainMenuNavEntryProps = IVerticalMenu &
	DetailedHTMLProps<HTMLAttributes<HTMLLIElement>, HTMLLIElement> & {
		currentVertical?: IBrandVertical;
		isFirst: boolean;
	};

const MainMenuNavEntry = memo(({ link, subMenu, id, currentVertical, className, isFirst, type: _, ...rest }: MainMenuNavEntryProps) => {
	const {
		text,
		url,
		// @ts-expect-error "type" apparently doesn't exist, but it's sent along all the same.
		type: _1,
		...linkProps
	} = link;
	const router = useRouter();
	const parentNode = useRef<HTMLLIElement>(null);
	const [interactive, setInteractive] = useState(false);
	const { toggleActiveSubMenu, activeSubMenu, menuIsOpen } = useMainMenuState((state) => ({
		menuIsOpen: state.menuIsOpen,
		activeSubMenu: state.activeSubMenu,
		toggleActiveSubMenu: state.toggleActiveSubMenu,
	}));
	const isMobile = !useMedia(lgCondition, !getIsMobile());

	useEffect(() => {
		// We know what we doin', boy.
		// eslint-disable-next-line react-hooks/set-state-in-effect
		setInteractive(true);
	}, []);

	const tracking = useMemo<CeNavigationTrackingData>(
		() => ({
			isBrand: true,
			hierarchy: {
				firstLevel: text,
			},
		}),
		[text],
	);

	const triggerCollapseCallback = useCallback(() => {
		if (parentNode?.current) triggerMenuCollapseOnElement(parentNode?.current as HTMLElement);
	}, []);

	const handleRootLevelClick = useCallback<MouseEventHandler<HTMLAnchorElement>>(
		(e) => {
			pushToDataLayer<CeBrandNavigationEvent>({
				event: 'brand_menu_click',
				...transformTrackingData(tracking.hierarchy),
			});

			const isMobile = getIsMobile();
			const requestedURL = String(e.currentTarget.href);

			// Stop the browser from doing anything stupid, since the link tag is a native <a>.
			e.preventDefault();
			e.stopPropagation();

			if (isMobile && subMenu?.length) {
				// Keep the menu active on route change
				window.sessionStorage.setItem('navigationMenuKeepActive', 'true');

				// Toggle the submenu, because we can
				toggleActiveSubMenu(activeSubMenu === link.id ? null : link.id);
			}

			// Make sure to collapse all sibling accordions
			parentNode.current?.parentElement?.querySelectorAll('li').forEach((el) => {
				if (el !== parentNode.current) {
					triggerMenuCollapseOnElement(el);
				}
			});

			const parsedURL = new URL(requestedURL);

			if (router.asPath !== parsedURL.pathname && currentVertical?.id !== id) {
				// Push the new URL into the router.
				router.push(requestedURL);
			}
		},
		[tracking.hierarchy, subMenu?.length, router, currentVertical?.id, id, toggleActiveSubMenu, activeSubMenu, link.id],
	);

	const isActive = currentVertical?.id === id || (!currentVertical?.type && isFirst);
	const isExpanded = activeSubMenu === link.id || (!isMobile && isActive);

	const hasSubMenu = subMenu && subMenu.length > 0;

	const hiddenFromSight = isMobile && interactive && !menuIsOpen;

	return (
		<li
			ref={parentNode}
			className={clsx(styles.rootLevelEntry, className)}
			{...rest}
		>
			<a
				{...linkProps}
				href={url}
				onClick={handleRootLevelClick}
				data-text={text}
				className={clsx(styles.rootLevelEntryLink, { [styles.accordionHeader]: hasSubMenu, [styles.active]: isActive })}
				aria-controls={hasSubMenu ? `${link.id}-sub` : undefined}
				aria-hidden={hiddenFromSight}
				tabIndex={hiddenFromSight ? -1 : undefined}
			>
				<span>{text}</span>
				{hasSubMenu && (
					<span className={clsx(styles.accordionToggleIcon, { [styles.open]: isExpanded })}>
						<ChevronDownIcon />
					</span>
				)}
			</a>
			{hasSubMenu && (
				<MainMenuSubmenu
					subMenu={subMenu as unknown as IMultiLevelNavigationNode[]}
					level={1}
					isExpanded={isExpanded}
					triggerCollapseCallback={triggerCollapseCallback}
					trackingHierarchy={undefined}
					aria-labelledby={link.id}
					id={`${link.id}-sub`}
				/>
			)}
		</li>
	);
});
MainMenuNavEntry.displayName = 'MainMenuNavEntry';

export default MainMenuNavEntry;
